import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, RotateCcw, X } from 'lucide-react';
import { subscriptFormula } from './equation';
import {
  DARK_INK,
  NowYouTry,
  VizCard,
  VIZ_BTN_GHOST,
  VIZ_BTN_PRIMARY,
  VIZ_FOCUS,
  cueHighlights,
  cueIsActive,
  usePrefersReducedMotion,
  useMountSequence,
  type VizCue,
} from '../vizShared';

// ReactionTypes (type 'reaction_types') — classify a reaction into one of the
// five canonical types (synthesis, decomposition, single replacement, double
// replacement, combustion). Each example animates its characteristic motion
// (two things joining, one thing splitting, partners swapping, fuel + O₂ →
// CO₂ + H₂O). The learner picks the type; immediate feedback + a one-line rule.
// config: { title?, reactions? }. Resilient: empty config uses the built-in set.

export type ReactionType =
  | 'synthesis'
  | 'decomposition'
  | 'single-replacement'
  | 'double-replacement'
  | 'combustion';

interface ReactionExample {
  equation: string;
  type: ReactionType;
}

export interface ReactionTypesConfig {
  title?: string;
  reactions?: ReactionExample[];
}

interface Props {
  config?: ReactionTypesConfig;
  onComplete?: (result?: unknown) => void;
  cue?: VizCue;
}

const TYPE_META: Record<ReactionType, { label: string; pattern: string; color: string; rule: string }> = {
  synthesis: {
    label: 'Synthesis',
    pattern: 'A + B → AB',
    color: '#5FB860',
    rule: 'Two or more reactants combine into a single product.',
  },
  decomposition: {
    label: 'Decomposition',
    pattern: 'AB → A + B',
    color: '#E0B341',
    rule: 'One compound breaks down into two or more products.',
  },
  'single-replacement': {
    label: 'Single replacement',
    pattern: 'A + BC → AC + B',
    color: '#3B82C4',
    rule: 'One element displaces another from a compound.',
  },
  'double-replacement': {
    label: 'Double replacement',
    pattern: 'AB + CD → AD + CB',
    color: '#A855C7',
    rule: 'Two compounds swap partners (ions exchange).',
  },
  combustion: {
    label: 'Combustion',
    pattern: 'CxHy + O₂ → CO₂ + H₂O',
    color: '#E0564B',
    rule: 'A fuel reacts with oxygen, releasing CO₂ and H₂O + energy.',
  },
};

const DEFAULT_REACTIONS: ReactionExample[] = [
  { equation: '2 H2 + O2 -> 2 H2O', type: 'synthesis' },
  { equation: '2 H2O -> 2 H2 + O2', type: 'decomposition' },
  { equation: 'Zn + 2 HCl -> ZnCl2 + H2', type: 'single-replacement' },
  { equation: 'AgNO3 + NaCl -> AgCl + NaNO3', type: 'double-replacement' },
  { equation: 'CH4 + 2 O2 -> CO2 + 2 H2O', type: 'combustion' },
  { equation: 'N2 + 3 H2 -> 2 NH3', type: 'synthesis' },
  { equation: 'CaCO3 -> CaO + CO2', type: 'decomposition' },
  { equation: 'Fe + CuSO4 -> FeSO4 + Cu', type: 'single-replacement' },
];

const ALL_TYPES: ReactionType[] = [
  'synthesis',
  'decomposition',
  'single-replacement',
  'double-replacement',
  'combustion',
];

export default function ReactionTypes({ config = {}, onComplete, cue }: Props) {
  const reduced = usePrefersReducedMotion();
  const hasCue = cueIsActive(cue);
  const reactions = useMemo(
    () => (config.reactions && config.reactions.length ? config.reactions : DEFAULT_REACTIONS),
    [config.reactions],
  );

  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState<ReactionType | null>(null);
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const interacted = useRef(false);

  const current = reactions[Math.min(idx, reactions.length - 1)];
  const correct = picked === current.type;

  // ── Self-demo: briefly preview the type categories (cycling the example
  // index so the learner sees each schematic) then settle on the first card
  // unanswered for them to try. ─────────────────────────────────────────────
  // The "preview" type drives which choice tile is spotlit during the demo.
  const [previewType, setPreviewType] = useState<ReactionType | null>(null);
  const [demoSettled, setDemoSettled] = useState(false);

  const demoSteps = useMemo(() => {
    // Find one example index per type (first match) to showcase.
    const order: { at: number; type: ReactionType; rIdx: number }[] = [];
    let at = 350;
    for (const t of ALL_TYPES) {
      const rIdx = reactions.findIndex((r) => r.type === t);
      if (rIdx >= 0) {
        order.push({ at, type: t, rIdx });
        at += 700;
      }
    }
    return order.map((o) => ({
      at: o.at,
      run: () => {
        setIdx(o.rIdx);
        setPreviewType(o.type);
        setPicked(null);
      },
    }));
  }, [reactions]);

  const demo = useMountSequence(demoSteps, {
    skip: reduced || hasCue,
    onSettle: () => {
      setIdx(0);
      setPicked(null);
      setPreviewType(null);
      setDemoSettled(true);
    },
  });
  useEffect(() => {
    if (demo.done && !reduced && !hasCue) {
      setIdx(0);
      setPicked(null);
      setPreviewType(null);
      setDemoSettled(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demo.done]);

  // ── Cue handling: highlight a type name (spotlights the tile + jumps to a
  // matching example); play=true runs the preview cycle. ────────────────────
  useEffect(() => {
    const h = cueHighlights(cue);
    if (h.length === 0) return;
    const match = ALL_TYPES.find(
      (t) => h.includes(t) || h.includes(TYPE_META[t].label.toLowerCase()),
    );
    if (match) {
      const rIdx = reactions.findIndex((r) => r.type === match);
      if (rIdx >= 0) setIdx(rIdx);
      setPreviewType(match);
      setPicked(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cue?.highlight]);

  const cancelDemo = () => {
    if (demo.running) demo.cancel();
    if (previewType) setPreviewType(null);
  };

  const choose = (t: ReactionType) => {
    cancelDemo();
    if (picked) return; // lock after answering
    if (!interacted.current) {
      interacted.current = true;
      onComplete?.({ type: 'reaction_types' });
    }
    setPicked(t);
    setScore((s) => ({ correct: s.correct + (t === current.type ? 1 : 0), total: s.total + 1 }));
  };

  const next = () => {
    cancelDemo();
    setPicked(null);
    setIdx((i) => (i + 1) % reactions.length);
  };

  const restart = () => {
    cancelDemo();
    setPicked(null);
    setIdx(0);
    setScore({ correct: 0, total: 0 });
  };

  return (
    <VizCard
      reducedMotion={reduced}
      eyebrow="Reactions · Classification"
      title={config.title ?? 'Classify the reaction'}
      subtitle="Watch the atoms rearrange, then pick the reaction type that matches."
      hint="Click a type to answer · the schematic shows the characteristic rearrangement."
      actions={
        <div className="flex items-center gap-2">
          <span className="text-xs text-ws-ink-3">
            Score {score.correct}/{score.total}
          </span>
          <button type="button" onClick={restart} className={VIZ_BTN_GHOST} aria-label="Restart and reset score">
            <RotateCcw size={12} /> Restart
          </button>
        </div>
      }
    >
      <div className="relative flex w-full flex-col gap-3">
        <NowYouTry show={demoSettled && !hasCue} />

        {/* Animated reaction stage */}
        <ReactionAnimation key={idx} type={current.type} equation={current.equation} reduced={reduced} />

        {/* Type choices */}
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
          {ALL_TYPES.map((t) => {
            const meta = TYPE_META[t];
            const isPick = picked === t;
            const isAnswer = current.type === t;
            const isPreview = previewType === t;
            let cls = 'border-ws-border bg-ws-tile text-ws-ink-2 hover:bg-ws-surface-2';
            if (picked) {
              if (isAnswer) cls = 'border-ws-success/60 bg-ws-success/10 text-ws-ink';
              else if (isPick) cls = 'border-ws-danger/60 bg-ws-danger/10 text-ws-ink';
              else cls = 'border-ws-border bg-ws-tile text-ws-ink-4';
            }
            return (
              <button
                key={t}
                type="button"
                onClick={() => choose(t)}
                disabled={!!picked}
                aria-label={`Classify as ${meta.label}`}
                className={`flex flex-col items-center gap-1 rounded-md border px-2 py-2 text-center transition-all duration-300 disabled:cursor-not-allowed ${VIZ_FOCUS} ${cls} ${
                  isPreview ? 'ring-2 ring-ws-accent ring-offset-1 ring-offset-ws-surface' : ''
                }`}
              >
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: meta.color }} />
                <span className="text-xs font-medium leading-tight">{meta.label}</span>
                <span className="font-mono text-[9px] text-ws-ink-4">{meta.pattern}</span>
                {picked && isAnswer && <Check size={13} className="text-ws-success" />}
                {picked && isPick && !isAnswer && <X size={13} className="text-ws-danger" />}
              </button>
            );
          })}
        </div>

        {/* Feedback */}
        <div className="flex min-h-[2.5rem] items-center justify-between gap-3 rounded-md border border-ws-border bg-ws-surface p-2.5">
          {picked ? (
            <span className="text-xs text-ws-ink-2">
              <span className={`font-semibold ${correct ? 'text-ws-success' : 'text-ws-danger'}`}>
                {correct ? 'Correct — ' : `It's ${TYPE_META[current.type].label}. `}
              </span>
              {TYPE_META[current.type].rule}
            </span>
          ) : (
            <span className="text-xs text-ws-ink-4">Pick the reaction type above.</span>
          )}
          {picked && (
            <button type="button" onClick={next} className={`${VIZ_BTN_PRIMARY} shrink-0`}>
              Next →
            </button>
          )}
        </div>
      </div>
    </VizCard>
  );
}

// ── Animated schematic for each reaction type ───────────────────────────────
// Abstract A/B/C/D blocks moving to illustrate the characteristic rearrangement,
// driven by a single 0→1 progress value on a CSS transition loop.
function ReactionAnimation({
  type,
  equation,
  reduced,
}: {
  type: ReactionType;
  equation: string;
  reduced: boolean;
}) {
  const [t, setT] = useState(reduced ? 1 : 0);
  useEffect(() => {
    if (reduced) {
      setT(1);
      return;
    }
    setT(0);
    const id = window.setTimeout(() => setT(1), 120);
    const loop = window.setInterval(() => setT((v) => (v >= 1 ? 0 : 1)), 2200);
    return () => {
      window.clearTimeout(id);
      window.clearInterval(loop);
    };
  }, [type, equation, reduced]);

  const meta = TYPE_META[type];
  const W = 360;
  const H = 96;

  return (
    <div className="rounded-md border border-ws-border bg-ws-surface-2 p-2">
      <svg viewBox={`0 0 ${W} ${H}`} className="h-24 w-full" role="img" aria-label={`${meta.label} animation`}>
        <Schematic type={type} t={t} w={W} h={H} reduced={reduced} />
        {/* yields arrow */}
        <g>
          <line x1={W / 2 - 14} y1={H / 2} x2={W / 2 + 14} y2={H / 2} stroke="var(--ws-ink-3)" strokeWidth={1.5} />
          <polygon
            points={`${W / 2 + 14},${H / 2 - 4} ${W / 2 + 20},${H / 2} ${W / 2 + 14},${H / 2 + 4}`}
            fill="var(--ws-ink-3)"
          />
        </g>
      </svg>
      <div className="mt-1 text-center font-mono text-sm font-semibold text-ws-ink">
        {subscriptFormula(equation.replace(/->/g, '→'))}
      </div>
    </div>
  );
}

function Atom({
  x,
  y,
  label,
  color,
  r = 14,
  reduced,
}: {
  x: number;
  y: number;
  label: string;
  color: string;
  r?: number;
  reduced?: boolean;
}) {
  return (
    <g
      style={{ transition: reduced ? 'none' : 'transform 0.9s cubic-bezier(.4,0,.2,1)' }}
      transform={`translate(${x},${y})`}
    >
      <circle r={r} fill={color} stroke="rgba(0,0,0,0.25)" strokeWidth={1} />
      <text textAnchor="middle" dy={4} fontSize={12} fontWeight={700} fill={DARK_INK}>
        {label}
      </text>
    </g>
  );
}

function Schematic({
  type,
  t,
  w,
  h,
  reduced,
}: {
  type: ReactionType;
  t: number;
  w: number;
  h: number;
  reduced?: boolean;
}) {
  const cy = h / 2;
  const lx = w * 0.18; // left cluster
  const rx = w * 0.78; // right cluster
  const A = '#5FB860';
  const B = '#3B82C4';
  const C = '#A855C7';
  const D = '#E0B341';
  const O = '#E0564B';

  switch (type) {
    case 'synthesis': {
      // A and B (apart) move together into AB on the right.
      const ax = lerp(lx - 16, rx - 14, t);
      const bx = lerp(lx + 16, rx + 14, t);
      return (
        <>
          <Atom x={ax} y={lerp(cy - 14, cy, t)} label="A" color={A} reduced={reduced} />
          <Atom x={bx} y={lerp(cy + 14, cy, t)} label="B" color={B} reduced={reduced} />
        </>
      );
    }
    case 'decomposition': {
      // AB (together, left) splits into A and B on the right.
      const ax = lerp(lx, rx - 14, t);
      const bx = lerp(lx, rx + 14, t);
      return (
        <>
          <Atom x={ax} y={lerp(cy, cy - 14, t)} label="A" color={A} reduced={reduced} />
          <Atom x={bx} y={lerp(cy, cy + 14, t)} label="B" color={B} reduced={reduced} />
        </>
      );
    }
    case 'single-replacement': {
      // A + BC → AC + B. A (free) displaces B, which is kicked out and drifts up.
      return (
        <>
          <Atom x={lerp(lx - 22, rx - 16, t)} y={cy} label="A" color={A} reduced={reduced} />
          <Atom x={lerp(lx + 22, rx + 22, t)} y={lerp(cy, cy - 18, t)} label="B" color={B} reduced={reduced} />
          <Atom x={lerp(lx + 44, rx + 14, t)} y={cy} label="C" color={C} reduced={reduced} />
        </>
      );
    }
    case 'double-replacement': {
      // AB + CD → AD + CB. A pairs with D (drifts right); C pairs with B.
      return (
        <>
          <Atom x={lerp(lx - 14, rx - 14, t)} y={cy} label="A" color={A} reduced={reduced} />
          <Atom x={lerp(lx + 14, rx + 14, t)} y={cy} label="B" color={B} reduced={reduced} />
          <Atom x={lerp(lx + 50, lx + 14, t)} y={cy} label="C" color={C} reduced={reduced} />
          <Atom x={lerp(lx + 78, rx - 14, t)} y={cy} label="D" color={D} reduced={reduced} />
        </>
      );
    }
    case 'combustion': {
      // CH4-ish fuel + O2 → CO2 + H2O (abstract: fuel + O join, emit).
      const fx = lerp(lx, rx - 16, t);
      const ox = lerp(lx + 30, rx + 14, t);
      return (
        <>
          <Atom x={fx} y={cy} label="F" color={D} reduced={reduced} />
          <Atom x={ox} y={lerp(cy + 16, cy, t)} label="O" color={O} reduced={reduced} />
          {/* spark / energy flicker */}
          <circle
            cx={w / 2}
            cy={cy}
            r={lerp(2, 7, t)}
            fill={O}
            opacity={t * 0.5}
            style={{ transition: reduced ? 'none' : 'all 0.9s ease' }}
          />
        </>
      );
    }
  }
}

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}
